import type { Drn } from '@duke-hq/drn/type';
import {
  type CancelFileUploadInput,
  type CancelFileUploadOutput,
  type CompleteFileUploadInput,
  type CompleteFileUploadOutput,
  type CreateFilesInput,
  type CreateFilesOutput,
  type CreatePartUploadUrlInput,
  type CreatePartUploadUrlOutput,
  type FileVisibility,
  UPLOAD_CHUNK_SIZE_MB,
} from '@duke-hq/svc-files-lib/schemas';
import { hashMultipartChunk } from '@duke-hq/svc-files-lib/hash';
import { uploadChunkPart } from './uploadChunkPart';

export interface ImperativeUploadResult {
  filename: string;
  fileDrn: Drn;
  url: string | null;
}

export interface ImperativeUploadActions {
  createFiles: (input: CreateFilesInput) => Promise<CreateFilesOutput>;
  createPartUploadUrl: (input: CreatePartUploadUrlInput) => Promise<CreatePartUploadUrlOutput>;
  completeFileUpload: (input: CompleteFileUploadInput) => Promise<CompleteFileUploadOutput>;
  cancelFileUpload: (input: CancelFileUploadInput) => Promise<CancelFileUploadOutput>;
}

export async function uploadFileImperative({
  file,
  actions,
  attachedToKind,
  attachedToId,
  visibility,
}: {
  file: File;
  actions: ImperativeUploadActions;
  attachedToKind?: string;
  attachedToId?: string;
  visibility?: FileVisibility;
}): Promise<ImperativeUploadResult> {
  const result = await actions.createFiles({
    attachedToKind,
    attachedToId,
    visibility,
    uploadFiles: [
      {
        name: file.name,
        mimetype: file.type,
        sizeInBytes: file.size,
      },
    ],
  });

  const fileData = result.files[0];
  if (!fileData) {
    throw new Error('No file data returned from createFiles');
  }

  try {
    const totalParts = Math.ceil(file.size / UPLOAD_CHUNK_SIZE_MB);
    const completedParts: { partNumber: number; checksumSHA256: string; etag: string }[] = [];

    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      const start = (partNumber - 1) * UPLOAD_CHUNK_SIZE_MB;
      const end = Math.min(start + UPLOAD_CHUNK_SIZE_MB, file.size);
      const chunk = file.slice(start, end);

      const checksumSHA256 = await hashMultipartChunk(chunk);

      const uploadUrl = await actions.createPartUploadUrl({
        fileDrn: fileData.drn,
        uploadId: fileData.uploadId,
        partNumber,
        checksumSHA256,
      });

      const { etag } = await uploadChunkPart({
        chunk,
        uploadUrl,
        checksumSHA256,
      });

      completedParts.push({ partNumber, checksumSHA256, etag });
    }

    const completed = await actions.completeFileUpload({
      fileDrn: fileData.drn,
      uploadId: fileData.uploadId,
      parts: completedParts,
    });

    return {
      filename: file.name,
      fileDrn: fileData.drn,
      url: completed.url,
    };
  } catch (err) {
    await actions
      .cancelFileUpload({ fileDrn: fileData.drn, uploadId: fileData.uploadId })
      .catch(() => {});
    throw err;
  }
}
